import type { Receipt } from "../api";
import { BUCKET_LABELS, ENTRY_KIND_LABELS } from "../api";
import { Badge, Button, Card, Stat, cx } from "../components";
import { formatDate, formatDateTime, formatEuros, isoDateTime } from "../format";
import { useCountUp } from "../motion";
import styles from "./AchatPage.module.css";

export type AchatRecuProps = {
  recu: Receipt;
  /**
   * L'utilisateur veut un **second** exemplaire du même pack. L'appelant
   * renouvelle alors la clé d'idempotence : sans ça, le second envoi serait vu
   * par le serveur comme le rejeu du premier et ne créditerait rien.
   */
  onRacheter: () => void;
};

/** Coche de validation. Décorative : le texte à côté dit déjà « crédité ». */
function Coche() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

/**
 * Reçu d'un achat abouti.
 *
 * Il porte les trois choses qu'on vient chercher après un paiement : ce qui a
 * été crédité, la trace comptable qui le prouve (l'écriture du grand livre), et
 * jusqu'à quand les tokens valent quelque chose.
 */
export function AchatRecu({ recu, onRacheter }: AchatRecuProps) {
  const { entry, pack } = recu;

  // Le montant crédité monte jusqu'à sa valeur : le mouvement du chiffre dit
  // « ceci vient d'entrer » mieux qu'une phrase. La dernière image écrit la
  // chaîne exacte reçue de l'API, donc rien n'est arrondi.
  const compteur = useCountUp<HTMLSpanElement>(entry.amount);

  return (
    <Card
      data-anim="card"
      // `title` de `Card` n'accepte qu'une chaîne : la prop se croise avec le
      // `title` de `HTMLAttributes`, qui est l'attribut d'infobulle. La coche
      // passe donc par `actions`.
      title="Pack crédité"
      subtitle={pack.label}
      actions={
        <span className={styles.pastilleSucces}>
          <Coche />
          <span className="lde-visually-hidden">Achat abouti</span>
        </span>
      }
      footer={
        <>
          <p className={styles.noteRejeu}>
            Si la page a été rechargée pendant l'achat, c'est ce même reçu qui
            s'affiche : rien n'est jamais crédité deux fois.
          </p>
          <div className={styles.actionsPanneau}>
            <Button variant="secondary" onClick={onRacheter}>
              Acheter un second exemplaire
            </Button>
          </div>
        </>
      }
    >
      <Stat
        label="Tokens crédités"
        value={<span ref={compteur} className="lde-tabular" />}
        unit="tokens"
        bucket={entry.bucket_to}
        emphasis
        highlight={entry.amount}
        highlightInitial
        hint={
          <>
            Valables jusqu'au{" "}
            <time dateTime={isoDateTime(recu.expires_at)}>
              {formatDate(recu.expires_at)}
            </time>
          </>
        }
      />

      <dl className={styles.lignesRecu}>
        <dt>Prix payé</dt>
        <dd className="lde-tabular">{formatEuros(pack.price_cents)}</dd>

        <dt>Écriture</dt>
        <dd>
          <Badge bucket={entry.bucket_to} size="sm" dot>
            {ENTRY_KIND_LABELS[entry.kind]} → {BUCKET_LABELS[entry.bucket_to]}
          </Badge>
        </dd>

        <dt>Référence</dt>
        {/* L'identifiant d'écriture est un entier 64 bits transporté en chaîne :
            il s'affiche tel quel, sans séparateur de milliers — c'est un numéro
            de séquence, pas un montant. */}
        <dd className={cx(styles.reference, "lde-tabular")}>#{entry.id}</dd>

        <dt>Enregistrée le</dt>
        <dd>
          <time dateTime={isoDateTime(entry.created_at)}>
            {formatDateTime(entry.created_at)}
          </time>
        </dd>

        <dt>Grille appliquée</dt>
        <dd>v{pack.rate_version}</dd>
      </dl>
    </Card>
  );
}
